/* LiveTicker — 30-second-poll live revenue strip that sits at the top of
 * TDB + ME division dashboards. Per-outlet breakdown, total-of-the-day,
 * green pulse dot when orders landed in the last 5 minutes, and a clear
 * "awaiting integration" state for ME until eZee API keys are wired.
 *
 * Tick-up animation is intentional — when a new order arrives mid-poll,
 * the number flashes amber for 1.5 s so the CEO literally SEES revenue
 * move without refreshing.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '../lib/api';
import { Activity, Radio, PauseCircle, X, FileSpreadsheet } from 'lucide-react';
import SyncNowButton from './SyncNowButton';

const INR_COMPACT = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const relTime = (mins) => {
  if (mins == null) return '—';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

export default function LiveTicker({ divisionId, pollMs = 30000, periodView = 'today', periodLabel = null, periodData = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const prevTotalRef = useRef(null);
  const [bumpTotal, setBumpTotal] = useState(false);
  // Order-audit drill-down (Shopify breakdown + per-order list).
  // Only available for divisions with a Shopify pipeline (= TDB today),
  // AND only relevant in 'today' period view (the audit endpoint is
  // date-scoped). For monthly/quarterly/YTD views we render a period
  // summary card with no live polling and no audit modal.
  const auditEnabled = divisionId === 'tdb' && periodView === 'today';
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditDate, setAuditDate] = useState(() => {
    const d = new Date();
    const ist = new Date(d.getTime() + (5.5 * 60 - d.getTimezoneOffset()) * 60000);
    return ist.toISOString().slice(0, 10);
  });

  const openAudit = async (forceDate) => {
    if (!auditEnabled) return;
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const r = await api.get(`/shopify/orders/audit/${divisionId}`, {
        params: { date: forceDate || auditDate },
      });
      setAuditData(r.data);
    } catch (e) {
      setAuditData({ error: e?.response?.data?.detail || e.message });
    } finally { setAuditLoading(false); }
  };

  useEffect(() => {
    // Skip the live polling fetch entirely in non-today views — the period
    // card below builds itself directly from the parent-provided `periodData`
    // (sourced from /api/divisions/{div}/quarter-stats which the parent
    // already fetches and re-fetches on toggle change). This is the fix for
    // the 19-May-2026 bug where toggling Today→Monthly visually changed the
    // surrounding period label but the ₹51,946 figure stayed frozen on the
    // live snapshot because LiveTicker ignored periodView.
    if (periodView !== 'today') {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchNow = async () => {
      try {
        const res = await api.get(`/daily-sales/today/${divisionId}`);
        if (cancelled) return;
        const next = res.data;
        // Flash amber when total goes up between polls
        if (prevTotalRef.current != null && next.total_revenue > prevTotalRef.current) {
          setBumpTotal(true);
          setTimeout(() => setBumpTotal(false), 1500);
        }
        prevTotalRef.current = next.total_revenue;
        setData(next); setError(null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Ticker offline');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchNow();
    if (isPaused) return () => { cancelled = true; };
    const iv = setInterval(fetchNow, pollMs);
    return () => { cancelled = true; clearInterval(iv); };
  }, [divisionId, pollMs, isPaused, periodView]);

  // Imperative refresh for the SyncNowButton onComplete callback —
  // re-pulls the ticker once eZee finishes the manual sync.
  const refetchTicker = useCallback(async () => {
    try {
      const res = await api.get(`/daily-sales/today/${divisionId}`);
      setData(res.data);
      prevTotalRef.current = res.data?.total_revenue ?? null;
    } catch { /* swallow — next poll will retry */ }
  }, [divisionId]);

  if (loading && !data) {
    return (
      <div className="rounded-xl p-3 mb-3" style={{ backgroundColor: '#F7F5F2', border: '1px dashed #D1CFCA' }}>
        <span className="text-[11px]" style={{ color: '#6B7280' }}>Loading live ticker…</span>
      </div>
    );
  }

  // ── Period-aware render path (Monthly / Quarterly / YTD) ───────────────
  // Uses the `periodData` (quarter-stats response) the parent already has.
  // No polling, no audit modal — this is an aggregate, not a live tile.
  if (periodView !== 'today') {
    const pTotal = periodData?.quarter_revenue || 0;
    const pTarget = periodData?.quarter_target || 0;
    const pPct = pTarget > 0 ? Math.round((pTotal / pTarget) * 100) : 0;
    // Prefer the API's `period_label` (e.g. "May 2026", "Q1 FY26", "FY 2026-2027")
    // over the raw `2026-05` we sometimes get from the parent's selectedMonth
    // dropdown. Format `YYYY-MM` → "Mon YYYY" as a last-resort fallback so the
    // label is always human-readable, never "2026-05" in tiny dark UI.
    const _formatYM = (ym) => {
      if (!ym || typeof ym !== 'string' || !/^\d{4}-\d{2}$/.test(ym)) return ym;
      const [y, m] = ym.split('-');
      const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${names[parseInt(m,10)-1]} ${y}`;
    };
    const pLabel = periodData?.period_label || periodData?.quarter_label || _formatYM(periodLabel) || _formatYM(periodView === 'monthly' ? periodLabel : null) || '—';
    const pRows = periodData?.outlet_breakdown || [];
    const pSubtitle = {
      monthly: 'Month-to-date · revenue across outlets',
      quarterly: 'Quarter-to-date · revenue across outlets',
      ytd: 'Year-to-date · revenue across outlets',
    }[periodView] || '';
    return (
      <div
        className="rounded-xl p-4 mb-4 relative overflow-hidden"
        style={{
          backgroundColor: '#1B2A4A', color: 'white',
          boxShadow: '0 10px 30px -10px rgba(27,42,74,.3)',
        }}
        data-testid={`live-ticker-${periodView}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.15em] font-semibold flex items-center gap-2 flex-wrap" style={{ color: '#9DB5CC' }}>
              <Activity size={11} className="inline mr-1" />
              {periodView.toUpperCase()} · {pLabel}
              {pTarget > 0 && (
                <span className="px-1.5 py-[1px] rounded text-[9px] font-bold tracking-[0.18em]"
                      style={{ backgroundColor: pPct >= 100 ? 'rgba(16,185,129,.18)' : pPct >= 60 ? 'rgba(251,191,36,.18)' : 'rgba(239,68,68,.18)',
                               color: pPct >= 100 ? '#10B981' : pPct >= 60 ? '#FBBF24' : '#EF4444' }}>
                  {pPct}% OF TARGET
                </span>
              )}
            </p>
            <div className="flex items-baseline gap-4 mt-1">
              <p className="text-3xl font-semibold" data-testid={`ticker-total-${periodView}`}>
                {INR_COMPACT(pTotal)}
              </p>
              {pTarget > 0 && (
                <span className="text-[12px]" style={{ color: '#9DB5CC' }}>
                  of {INR_COMPACT(pTarget)} target
                </span>
              )}
            </div>
            <p className="text-[10.5px] mt-1" style={{ color: '#9DB5CC' }}>{pSubtitle}</p>
          </div>
        </div>
        {/* Per-outlet breakdown for the selected period */}
        {pRows.length > 0 && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {pRows.slice(0, 8).map((r, i) => {
              const rPct = r.target > 0 ? Math.round((r.actual / r.target) * 100) : null;
              return (
                <div key={r.label || i}
                     className="rounded-lg p-2.5"
                     style={{ backgroundColor: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.05)' }}
                     data-testid={`ticker-${periodView}-row-${(r.label || '').replace(/\s+/g, '-').toLowerCase()}`}>
                  <p className="text-[12px] font-medium truncate">{r.label}</p>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <p className="text-[14px] font-semibold">{INR_COMPACT(r.actual)}</p>
                    {rPct != null && (
                      <span className="text-[10px]"
                            style={{ color: rPct >= 100 ? '#10B981' : rPct >= 60 ? '#FBBF24' : '#9DB5CC' }}>
                        {rPct}%
                      </span>
                    )}
                  </div>
                  {r.target > 0 && (
                    <p className="text-[10px]" style={{ color: '#9DB5CC' }}>of {INR_COMPACT(r.target)}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {pRows.length === 0 && (
          <p className="mt-3 text-[11px]" style={{ color: '#9DB5CC' }}>
            No outlet-level breakdown for this period yet.
          </p>
        )}
      </div>
    );
  }

  if (error && !data) return null;
  if (!data) return null;

  const dateFmt = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };
  const anyHot = (data.rows || []).some(r => r.is_hot);
  const sourceLabel = {
    shopify: 'via Shopify',
    ezee: 'via eZee',
    manual: 'manual entries',
    mixed: 'mixed sources',
    none: 'no data yet',
  }[data.data_source] || '';

  // Freshness pill — 4-letter status the operator can read at a glance.
  // LIVE = at least one row inside the live-threshold window (10 min)
  // LATE = data exists but stale (>10 min since last order)
  // NONE = no orders today (hide the pill on awaiting_integration banners)
  const freshness = data.freshness || { status: 'none', latest_order_age_minutes: null };
  const freshnessStyles = {
    live: { bg: 'rgba(16,185,129,.18)', fg: '#10B981', label: 'LIVE' },
    late: { bg: 'rgba(251,191,36,.18)', fg: '#FBBF24', label: 'LATE' },
    none: { bg: 'rgba(156,163,175,.18)', fg: '#9CA3AF', label: 'NONE' },
  }[freshness.status] || { bg: 'rgba(156,163,175,.18)', fg: '#9CA3AF', label: 'NONE' };
  const freshnessTitle = (() => {
    const m = freshness.latest_order_age_minutes;
    if (m == null) return 'No orders today yet';
    if (m < 1) return 'Last order: just now';
    if (m < 60) return `Last order: ${m} min ago`;
    if (m < 1440) return `Last order: ${Math.floor(m / 60)}h ${m % 60}m ago`;
    return `Last order: ${Math.floor(m / 1440)}d ago`;
  })();

  return (
    <div
      className="rounded-xl p-4 mb-4 relative overflow-hidden"
      style={{
        backgroundColor: '#1B2A4A', color: 'white',
        boxShadow: anyHot ? '0 0 0 2px rgba(16,185,129,.35), 0 10px 30px -10px rgba(27,42,74,.6)' : '0 10px 30px -10px rgba(27,42,74,.3)',
        transition: 'box-shadow .3s ease',
      }}
      data-testid="live-ticker"
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.15em] font-semibold flex items-center gap-2 flex-wrap" style={{ color: '#9DB5CC' }}>
            <span>
              <Activity size={11} className="inline mr-1" />
              TODAY ({dateFmt(data.date)}) · LIVE {sourceLabel && `· ${sourceLabel}`}
            </span>
            <span
              data-testid="ticker-freshness-pill"
              className="px-1.5 py-[1px] rounded text-[9px] font-bold tracking-[0.18em]"
              style={{ backgroundColor: freshnessStyles.bg, color: freshnessStyles.fg }}
              title={freshnessTitle}
            >
              {freshnessStyles.label}
            </span>
          </p>
          <div className="flex items-baseline gap-4 mt-1">
            <p
              className="text-3xl font-semibold"
              style={{
                transition: 'color .6s ease, text-shadow .6s ease',
                color: bumpTotal ? '#FBBF24' : 'white',
                textShadow: bumpTotal ? '0 0 20px rgba(251,191,36,0.4)' : 'none',
                cursor: auditEnabled ? 'pointer' : 'default',
              }}
              data-testid="ticker-total"
              role={auditEnabled ? 'button' : undefined}
              tabIndex={auditEnabled ? 0 : -1}
              title={auditEnabled ? 'Click to audit which orders are included today' : undefined}
              onClick={() => auditEnabled && openAudit()}
            >
              {INR_COMPACT(data.total_revenue)}
            </p>
            {auditEnabled && (
              <button
                onClick={() => openAudit()}
                data-testid="ticker-audit-btn"
                className="text-[11px] underline-offset-2 hover:underline flex items-center gap-1"
                style={{ color: '#9DB5CC' }}>
                <FileSpreadsheet size={11} /> Audit orders
              </button>
            )}
            {data.total_orders > 0 && (
              <span className="text-[12px]" style={{ color: '#9DB5CC' }}>
                {data.total_orders} order{data.total_orders === 1 ? '' : 's'}
              </span>
            )}
            {anyHot && (
              <span className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: '#10B981' }}>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: '#10B981' }}></span>
                  <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: '#10B981' }}></span>
                </span>
                Active now
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {divisionId === 'me' && (
            <SyncNowButton
              divisionId="me"
              variant="dark"
              testid="ticker-sync-btn"
              onComplete={refetchTicker}
            />
          )}
          <button
            onClick={() => setIsPaused(p => !p)}
            className="flex items-center gap-1 px-2 py-1 rounded text-[10px]"
            style={{
              backgroundColor: isPaused ? 'rgba(251,191,36,.15)' : 'rgba(255,255,255,.08)',
              color: isPaused ? '#FBBF24' : '#9DB5CC',
            }}
            data-testid="ticker-pause-btn"
            title={isPaused ? 'Resume live polling' : 'Pause live polling'}
          >
            {isPaused ? <Radio size={10} /> : <PauseCircle size={10} />}
            {isPaused ? 'Paused' : 'Live · 30 s'}
          </button>
        </div>
      </div>

      {/* Awaiting-integration banner (ME before eZee connects) */}
      {data.awaiting_integration && (
        <div className="mt-3 rounded-md p-2.5 text-[11px]"
             style={{ backgroundColor: 'rgba(251,191,36,.12)', color: '#FCD34D' }}
             data-testid="ticker-awaiting">
          ⚠️ {data.message}
        </div>
      )}

      {/* Per-outlet rows */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {(data.rows || []).slice(0, 9).map(r => (
          <div key={r.outlet}
               className="rounded-lg p-2.5 flex items-center justify-between"
               style={{
                 backgroundColor: r.is_hot ? 'rgba(16,185,129,.12)' : 'rgba(255,255,255,.06)',
                 border: r.is_hot ? '1px solid rgba(16,185,129,.4)' : '1px solid rgba(255,255,255,.05)',
               }}
               data-testid={`ticker-row-${r.outlet.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                {r.is_hot && (
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: '#10B981' }}></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ backgroundColor: '#10B981' }}></span>
                  </span>
                )}
                <p className="text-[12px] font-medium truncate">{r.outlet}</p>
              </div>
              <p className="text-[10px]" style={{ color: '#9DB5CC' }}>
                {r.status && <span>{r.status} · </span>}
                {r.orders > 0 && <span>{r.orders} ord{r.covers > 0 && ` · ${r.covers} cov`} · </span>}
                {r.last_order_at ? `last ${relTime(r.minutes_since)}` : 'no orders today'}
              </p>
            </div>
            <p className="text-[14px] font-semibold ml-3">{INR_COMPACT(r.revenue)}</p>
          </div>
        ))}
      </div>

      {/* Order-audit modal — clicking the headline number opens this. */}
      {auditOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8"
          style={{ background: 'rgba(11,17,32,0.78)', backdropFilter: 'blur(4px)' }}
          data-testid="ticker-audit-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setAuditOpen(false); }}
        >
          <div className="w-full max-w-5xl rounded-xl shadow-2xl"
               style={{ background: '#F7F5F2', color: '#1B2A4A', maxHeight: 'calc(100vh - 4rem)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#E5E1DC' }}>
              <div>
                <div className="text-[10px] tracking-[0.15em] font-semibold" style={{ color: '#8A4B00' }}>
                  ORDER AUDIT · {(auditData && auditData.date) || auditDate}
                </div>
                <div className="text-[18px] font-semibold mt-0.5">What's inside today's revenue tile</div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={auditDate}
                  onChange={(e) => setAuditDate(e.target.value)}
                  data-testid="ticker-audit-date"
                  className="px-2 py-1 text-[12px] rounded border"
                  style={{ borderColor: '#E5E1DC', background: 'white' }}
                />
                <button
                  onClick={() => openAudit()}
                  className="px-3 py-1 text-[11px] rounded font-medium"
                  style={{ background: '#1B2A4A', color: 'white' }}
                  data-testid="ticker-audit-refresh">
                  Refresh
                </button>
                <button
                  onClick={() => setAuditOpen(false)}
                  data-testid="ticker-audit-close"
                  className="p-1.5 rounded hover:bg-black/5">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-5 py-4" style={{ flex: 1 }}>
              {auditLoading && <div className="text-[12px]" style={{ color: '#6B7280' }}>Loading…</div>}
              {auditData?.error && (
                <div className="text-[12px] p-3 rounded" style={{ background: '#FEF2F2', color: '#7a1f1f' }}>
                  {auditData.error}
                </div>
              )}
              {auditData && !auditData.error && (
                <>
                  {/* Shopify-style total sales breakdown */}
                  <div className="rounded-lg p-4 mb-4" style={{ background: 'white', border: '1px solid #E5E1DC' }}>
                    <div className="text-[11px] font-semibold mb-2" style={{ color: '#8A4B00' }}>
                      TOTAL SALES BREAKDOWN
                    </div>
                    {[
                      ['Gross sales', auditData.shopify_breakdown.gross_sales],
                      ['Discounts', -auditData.shopify_breakdown.discounts],
                      ['Returns', -auditData.shopify_breakdown.returns],
                      ['Net sales', auditData.shopify_breakdown.net_sales, true],
                      ['Shipping charges', auditData.shopify_breakdown.shipping],
                      ['Return fees', auditData.shopify_breakdown.return_fees],
                      ['Taxes', auditData.shopify_breakdown.taxes],
                      ['Total sales', auditData.shopify_breakdown.total_sales, true],
                    ].map(([label, val, bold], i) => (
                      <div key={i}
                           className="flex justify-between py-1.5"
                           style={{ borderBottom: i < 7 ? '1px solid #F0EDE8' : 'none' }}>
                        <span className="text-[12.5px]" style={{ color: bold ? '#1B2A4A' : '#4B5563', fontWeight: bold ? 600 : 400 }}>{label}</span>
                        <span className="text-[13px]" style={{ color: '#1B2A4A', fontWeight: bold ? 700 : 500 }}>
                          {val < 0 ? '-' : ''}₹{Math.abs(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Funnel summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 text-[11px]">
                    {[
                      ['In tile', auditData.orders_counted_in_tile, '#10B981'],
                      ['Total orders', auditData.orders_total, '#1B2A4A'],
                      ['Skipped — unpaid', auditData.orders_skipped_unpaid, '#9CA3AF'],
                      ['With refund', auditData.orders_with_refund, '#D97706'],
                    ].map(([l, n, c]) => (
                      <div key={l} className="rounded p-2.5" style={{ background: 'white', border: '1px solid #E5E1DC' }}>
                        <div style={{ color: '#6B7280' }}>{l}</div>
                        <div className="text-[16px] font-bold" style={{ color: c }}>{n}</div>
                      </div>
                    ))}
                  </div>

                  {/* By-outlet bucketing */}
                  {Object.keys(auditData.by_outlet || {}).length > 0 && (
                    <div className="rounded-lg p-3 mb-4" style={{ background: 'white', border: '1px solid #E5E1DC' }}>
                      <div className="text-[11px] font-semibold mb-2" style={{ color: '#8A4B00' }}>BY OUTLET</div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                        {Object.entries(auditData.by_outlet).map(([outlet, v]) => (
                          <div key={outlet} className="rounded p-2"
                               style={{ background: outlet === 'Online / B2B' ? '#FFF8EE' : '#F7F5F2',
                                         border: outlet === 'Online / B2B' ? '1px solid #F4D8A8' : '1px solid #E5E1DC' }}>
                            <div style={{ color: '#6B7280' }}>{outlet}</div>
                            <div className="font-bold text-[13px]" style={{ color: '#1B2A4A' }}>
                              ₹{v.total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                            </div>
                            <div className="text-[10px]" style={{ color: '#9CA3AF' }}>{v.count} order{v.count === 1 ? '' : 's'}</div>
                          </div>
                        ))}
                      </div>
                      {auditData.by_outlet['Online / B2B'] && auditData.by_outlet['Online / B2B'].count > 0 && (
                        <div className="mt-2 text-[10.5px]" style={{ color: '#8A4B00' }}>
                          Some orders fell into <b>Online / B2B</b> — usually means the shipping city wasn't recognised by our outlet rules. Hover the city column to spot which ones.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Per-order table */}
                  <div className="rounded-lg overflow-hidden" style={{ background: 'white', border: '1px solid #E5E1DC' }}>
                    <div className="text-[11px] font-semibold px-3 py-2 flex items-center justify-between"
                         style={{ background: '#F7F5F2', color: '#8A4B00', borderBottom: '1px solid #E5E1DC' }}>
                      <span>{auditData.orders.length} ORDERS</span>
                      <span style={{ color: '#9CA3AF' }}>green = counted in tile · grey = skipped</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11.5px]">
                        <thead style={{ background: '#FAF8F5', color: '#6B7280' }}>
                          <tr>
                            <th className="text-left px-3 py-2 font-medium">Order</th>
                            <th className="text-left px-3 py-2 font-medium">Status</th>
                            <th className="text-left px-3 py-2 font-medium">City</th>
                            <th className="text-left px-3 py-2 font-medium">Bucket</th>
                            <th className="text-right px-3 py-2 font-medium">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditData.orders.map((o, idx) => (
                            <tr key={o.order_id || idx}
                                style={{
                                  background: o.counted_in_tile ? 'white' : '#FAFAF9',
                                  borderTop: idx === 0 ? 'none' : '1px solid #F0EDE8',
                                  opacity: o.counted_in_tile ? 1 : 0.7,
                                }}>
                              <td className="px-3 py-1.5">
                                <div className="font-medium" style={{ color: '#1B2A4A' }}>{o.name || o.order_id}</div>
                                <div style={{ color: '#9CA3AF', fontSize: 10 }}>{o.email || '—'}</div>
                              </td>
                              <td className="px-3 py-1.5">
                                <span className="px-1.5 py-0.5 rounded text-[10px]"
                                      style={{
                                        background: o.financial_status === 'paid' ? '#DCFCE7' : '#F3F4F6',
                                        color: o.financial_status === 'paid' ? '#15803D' : '#6B7280',
                                      }}>
                                  {o.financial_status || '—'}
                                </span>
                                {o.refunded > 0 && (
                                  <span className="ml-1 px-1.5 py-0.5 rounded text-[10px]"
                                        style={{ background: '#FED7AA', color: '#9A3412' }}>
                                    refunded ₹{o.refunded.toLocaleString('en-IN')}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-1.5" style={{ color: '#4B5563' }}>{o.city || '—'}</td>
                              <td className="px-3 py-1.5">
                                <span className="text-[11px]"
                                      style={{ color: o.outlet === 'Online / B2B' ? '#8A4B00' : '#1B2A4A',
                                                fontWeight: o.outlet === 'Online / B2B' ? 600 : 500 }}>
                                  {o.outlet}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-right font-semibold" style={{ color: '#1B2A4A' }}>
                                ₹{o.total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                            </tr>
                          ))}
                          {auditData.orders.length === 0 && (
                            <tr><td colSpan={5} className="text-center py-6 text-[12px]" style={{ color: '#9CA3AF' }}>
                              No Shopify orders received for this date yet.
                            </td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
